import { ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChildren, ViewChild, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs'
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { TimeAndBillingService } from 'src/app/time-and-billing/time-and-billing.service';
import { SearchService } from 'src/app/api/search.service';
import { DetailsPage } from 'src/app/projects/details/details.page';
import { Table } from 'primeng/table';
import { CreatePage } from 'src/app/projects/create/create.page';
import { SearchComponent } from '../../search/search.component';
import { ProjectsService } from 'src/app/api/projects.service';
import { AssignPage } from 'src/app/projects/assign/assign.page';
import { ManageComponent } from 'src/app/components/tags/manage/manage.component';
import { ManagerComponent } from '../../groups/manager/manager.component';
import { TemplateBookComponent } from '../template-book/template-book.component';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { SchedulePage } from 'src/app/projects/schedule/schedule.page';
import { FilterService } from 'primeng/api';
import * as isBetween from 'dayjs/plugin/isBetween';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { CookieService } from 'ngx-cookie-service';
import { TaskStatus } from '../../tasks/TaskStatus';
import { ProjectHealthService } from 'src/app/api/project-health.service';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';
import { DetailsComponent as TeamMemberDetailsPage } from '../../team-members/details/details.component';
import { TablePdfExportService } from 'src/app/api/table-pdf-export.service';
import { TableExcelExportService } from 'src/app/api/table-excel-export.service';

@Component({
  selector: 'app-project-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {

  @ViewChildren(Table) tables: QueryList<Table>;
  @ViewChild('dateOverlay') dateOverlay: OverlayPanel;
  @ViewChild('estimatedTimeOverlay') estimatedTimeOverlay: OverlayPanel;
  tags: any[];
  groups: any[];
  @Input() column_picker_enabled = false;
  @Input() projects: any = null;
  @Input() non_grouped_projects: any = null;
  @Input() inclusive_dates = true;
  @Input() date_filter_type: 'due' | 'start' | 'happenning' = 'due';

  // Sorts by whichever date column date_filter_type is actually filtering on (earliest first,
  // see [sortOrder] on the p-table) - 'happenning' filters by internal_end_date too (see
  // GetData()'s use of date_filter_type below), so it sorts the same way as 'due'.
  get sortField(): string {
    return this.date_filter_type === 'start' ? 'internal_start_date' : 'internal_end_date';
  }
  @Input() show_paging: any = null;
  @Input() show_expander = true;
  @Input() show_checkbox = false;
  @Input() edit_mode = true;
  // Hides just the "Create Projects" button (and the date-range filter row stays put) - used on pages
  // like a contact's or company's own Projects tab, where creating a project should happen from the
  // Projects page itself, not from inside their details.
  @Input() hide_create_button = false;
  // On a contact's / company's Projects tab: the create button assigns that contact / company to the new project
  @Input() create_client: any = null;
  @Input() create_company: any = null;
  @Input() url_mod = '';
  @Input() show_manage_templates = false;
  @Input() show_search = true;
  @Input() show_completed_projects = false;
  @Input() allow_hash_set = false;
  @Input() client_guid
  @Input() ignore_cookies = false;
  /**
   * Array of column keys to hide. Example: ['tags','groups'] to hide tags and groups columns
   */
  @Input() hideColumns: string[] = [];
  max_per_page = 100;
  @Output() projectModified = new EventEmitter<{ data: any }>();
  today: Date;
  @Input() selectedProjects = [];
  @Input() max_allowed_selections: number = 9999999999;
  @Output() selectedProjectEvent = new EventEmitter<any>();
  // Shows the multi-select toggle button (next to Search) that lets the user opt into the
  // checkbox column / "Mark Complete" / "Change Internal Status" toolbar. Kept separate from
  // show_checkbox so pages that reuse the checkbox column purely to pick projects for some other
  // purpose (e.g. projects/select) don't also get the toggle button or bulk-modify actions.
  @Input() allow_bulk_actions = false;
  // On/off state of the multi-select toggle button. Off by default - the checkbox column and bulk
  // toolbar only appear once the user explicitly turns this on.
  bulk_select_active = false;
  bulk_internal_status_guid: string = '';
  bulk_assigned_member_guid: string = '';
  bulk_tag_guid: string = '';
  bulk_group_guid: string = '';

  // Whether the checkbox column should render - either because a caller always wants it
  // (show_checkbox, e.g. the project picker) or because the user turned on multi-select mode.
  get show_selection_column(): boolean {
    return this.show_checkbox || (this.allow_bulk_actions && this.bulk_select_active);
  }

  ToggleBulkSelectMode() {
    this.bulk_select_active = !this.bulk_select_active;
    if (!this.bulk_select_active) this.ClearSelectedProjects();
  }

  isProjectSelectionDisabled(project: any): boolean {
    if (this.selectedProjects.length < this.max_allowed_selections) return false;
    return !this.selectedProjects.find((p: any) => p.guid === project.guid);
  }


  loaded_projects = false;
  backgroundLoading = false;
  private _loadGeneration = 0;
  @Input() start_date_iso;
  @Input() end_date_iso;
  @Input() groupView = true;
  @Input() hide_empty_groups = false;
  // Lets Group View group by something other than tags/groups (Project Health, Companies, Contacts,
  // or a two-level combination of Groups with Companies/Contacts - see UpdateHealthGroups/
  // UpdateSingleDimensionGroups/UpdateCombinedGroups) - off by default since most embeds of this
  // table (client/company details, home page, etc.) have no use for it; only the main Projects page
  // opts in.
  @Input() group_type_change = false;
  group_by: 'groups' | 'health' | 'companies' | 'contacts' | 'clients' | 'groups_companies' | 'companies_groups' | 'groups_contacts' | 'contacts_groups' | 'clients_groups' = 'groups';
  // Each is built up lazily (one bucket object per guid, created on first sight) and reused
  // afterwards rather than reassigned wholesale, so a bucket's .expanded state survives future
  // recomputes - mirrors how this.groups itself is never reassigned wholesale in UpdateGroupsCount,
  // only its .objects/.projects fields are refreshed.
  healthGroups: any[] = [];
  companyGroups: any[] = [];
  contactGroups: any[] = [];
  groupsCompaniesGroups: any[] = [];
  companiesGroupsGroups: any[] = [];
  groupsContactsGroups: any[] = [];
  contactsGroupsGroups: any[] = [];
  // "Clients": contacts and companies together, whether or not they are marked as a client
  clientGroups: any[] = [];
  clientsGroupsGroups: any[] = [];
  get activeGroups(): any[] {
    switch (this.group_by) {
      case 'health': return this.healthGroups;
      case 'companies': return this.companyGroups;
      case 'contacts': return this.contactGroups;
      case 'clients': return this.clientGroups;
      case 'clients_groups': return this.clientsGroupsGroups;
      case 'groups_companies': return this.groupsCompaniesGroups;
      case 'companies_groups': return this.companiesGroupsGroups;
      case 'groups_contacts': return this.groupsContactsGroups;
      case 'contacts_groups': return this.contactsGroupsGroups;
      default: return this.groups;
    }
  }
  // Companies/Contacts buckets (flat or nested) are only ever created for an entity that already has
  // at least one matching project (see UpdateSingleDimensionGroups/UpdateCombinedGroups), so they can
  // never actually be empty - Hide Empty Groups is forced on and locked whenever one of these modes
  // is active, so the toggle can't be flipped to something that wouldn't change anything anyway (and
  // stays correctly "on" for the real, legitimately-sometimes-empty group buckets these modes mix in).
  get IsCompanyOrContactGrouping(): boolean {
    return this.group_by !== 'groups' && this.group_by !== 'health';
  }
  OnGroupByChange() {
    if (this.IsCompanyOrContactGrouping) this.hide_empty_groups = true;
    this.UpdateFilterCookies();
  }
  // True for a top-level bucket in one of the four two-level modes ('groups_companies' etc. - always
  // contain an underscore, unlike the single-dimension mode names) - those buckets deliberately have
  // an empty .projects (see UpdateCombinedGroups) since everything they contain is already shown
  // under their nested inner buckets, so neither the flat <p-table> nor the "no projects" empty-state
  // should render for them. Left out of plain "Groups" mode on purpose - there, a parent tag-group
  // with 0 *direct* projects but some in a subgroup is genuinely worth flagging as empty at its own
  // level, since direct membership is a real, independent relationship for actual tag groups.
  IsOuterCombinedBucket(group: any): boolean {
    return !!group && this.group_by.includes('_') && !group.parent_guid;
  }
  non_grouped_objects = [];
  non_grouped_expanded = false;
  show_all_projects = true;
  show_in_progress_only = false;
  show_overdue_only = false;
  project_person_filter: 'all' | 'worker' | 'follower' | 'team_member' = 'all';
  // "View for a Team Member" picker (see ToggleTeamMemberPicker/OnTeamMemberFilterChange) - lets you
  // view projects as if you were any team member, not just yourself, picking their role the same
  // way the Worker/Follower buttons do for the current user.
  show_team_member_picker = false;
  view_team_member_guid = '';
  view_team_member_role: 'worker' | 'follower' = 'worker';
  // Search functionality
  is_searching = false;
  all_projects: any[] = []; // Store original unfiltered projects
  current_search_query: any = null;
  team_member: any = null;
  view_client: any = null;
  view_company: any = null;

  // Inline date editing state
  inline_edit_project: any = null;
  inline_edit_field: string = null;

  // Inline estimated time editing state
  inline_edit_est_project: any = null;
  inline_edit_est_hours = 0;
  inline_edit_est_minutes = 0;
  inline_edit_date: Date = null;
  inline_edit_min_date: Date = null;
  inline_edit_max_date: Date = null;
  inline_edit_disabled_tooltip: string = null;
  projectInternalStatuses: any[] = [];
  clients_roles: any[] = [];
  activeProjectFilterLabels: string[] = [];
  matchModeOptions = [
    { label: 'Includes Any', value: 'includesAny' },
    { label: 'Includes All', value: 'includesAll' },
  ];
  singleMatchModeOptions = [
    { label: 'Includes Any', value: 'singleIncludesAny' },
    { label: 'Includes All', value: 'singleIncludesAll' },
  ];

  // Not private - table.component.html binds this straight into app-filter-presets'
  // [fieldLabelMap] so saved-filter tooltips can describe filters in the same plain-English labels
  // the "Column filters active" indicator already uses.
  readonly projectFilterLabelMap: { [field: string]: string } = {
    title: 'Title',
    internal_status: 'Internal Status',
    status_label: 'System Status',
    linked_name: 'Linked To',
    client_year_end_date: 'Client Year End Date',
    period_start_date: 'Period Start Date',
    period_end_date: 'Period End Date',
    team_leader_guid: 'Project Leader',
    progress: 'Progress',
    health_label: 'Health',
    internal_start_date: 'Project Start Date',
    internal_end_date: 'Project End Date',
    completed_date: 'Completed Date',
    last_client_document_upload: 'Last Client Upload',
    document_request_complete: 'Document Request Complete',
    tags: 'Tags',
    groups: 'Groups',
  };

  onProjectTableFilter(event: any) {
    this.activeProjectFilterLabels = this.computeActiveFilterLabels(event.filters, this.projectFilterLabelMap);
  }

  clearProjectFilters() {
    this.tables?.forEach(t => this.clearTableFilters(t));
    this.activeProjectFilterLabels = [];
  }

  // Saved filter presets (see FilterPresetsComponent) - captures both the PrimeNG column filters (off
  // the Table instance, same as clearProjectFilters reads) AND the "above the table" toggle buttons
  // that also narrow down what's shown (Uncompleted/In Progress/Overdue/All, My Role Filter, View for
  // a Team Member, Group By, Show/Hide Empty Groups) - the exact same field set UpdateFilterCookies
  // already persists, minus groupView (Group/Table View itself is excluded on request - it's a display
  // mode, not a filter) and hideColumns (column visibility, not row filtering - the Column Picker
  // already owns that separately). Arrow function so it stays bound to this component when handed to
  // the child as a plain function reference (see [getCurrentFilters]="GetCurrentTableFilters" in
  // table.component.html).
  GetCurrentTableFilters = (): any => {
    return {
      columnFilters: (this.tables?.first as any)?.filters ?? null,
      toolbar: {
        show_completed_projects: this.show_completed_projects,
        show_in_progress_only: this.show_in_progress_only,
        show_overdue_only: this.show_overdue_only,
        hide_empty_groups: this.hide_empty_groups,
        project_person_filter: this.project_person_filter,
        view_team_member_guid: this.view_team_member_guid,
        view_team_member_role: this.view_team_member_role,
        group_by: this.group_by,
      }
    };
  };

  // Grouped view renders one <p-table> per group (see *ngFor="let group of activeGroups"), each with
  // its own independent filters object, so a preset needs applying to all of them - same as
  // clearTableFilters/clearProjectFilters already do. Accepts either the current {columnFilters,
  // toolbar} shape or a bare PrimeNG filters object (presets saved before the toolbar was included).
  ApplyTableFilters(saved: any) {
    const isWrapped = !!saved && (('columnFilters' in saved) || ('toolbar' in saved));
    const columnFilters = isWrapped ? saved?.columnFilters : saved;
    const toolbar = isWrapped ? saved?.toolbar : null;

    const applied = columnFilters ? JSON.parse(JSON.stringify(columnFilters)) : {};
    this.tables?.forEach(t => {
      (t as any).filters = JSON.parse(JSON.stringify(applied));
      (t as any)._filter();
    });
    this.activeProjectFilterLabels = this.computeActiveFilterLabels(applied, this.projectFilterLabelMap);

    if (toolbar) {
      if (typeof toolbar.show_completed_projects === 'boolean') this.show_completed_projects = toolbar.show_completed_projects;
      if (typeof toolbar.show_in_progress_only === 'boolean') this.show_in_progress_only = toolbar.show_in_progress_only;
      if (typeof toolbar.show_overdue_only === 'boolean') this.show_overdue_only = toolbar.show_overdue_only;
      if (typeof toolbar.hide_empty_groups === 'boolean') this.hide_empty_groups = toolbar.hide_empty_groups;
      if (toolbar.project_person_filter) {
        this.project_person_filter = toolbar.project_person_filter;
        this.show_team_member_picker = toolbar.project_person_filter === 'team_member';
      }
      if (toolbar.view_team_member_guid !== undefined) this.view_team_member_guid = toolbar.view_team_member_guid;
      if (toolbar.view_team_member_role) this.view_team_member_role = toolbar.view_team_member_role;
      if (this.group_type_change && toolbar.group_by) {
        this.group_by = toolbar.group_by;
        if (this.IsCompanyOrContactGrouping) this.hide_empty_groups = true;
      }
      this.UpdateGroupsCount();
      this.UpdateFilterCookies();
    }
  }

  // Plain-English description of the "above the table" toggle state saved alongside column filters
  // (see GetCurrentTableFilters) - handed to app-filter-presets as [describeToolbarState] so a saved
  // filter's hover tooltip covers everything the preset actually restricts, not just column filters.
  // Arrow function for the same reason GetCurrentTableFilters is - passed by reference to the child.
  DescribeToolbarState = (toolbar: any): string[] => {
    const parts: string[] = [];
    if (toolbar.show_overdue_only) parts.push('Status: Overdue Projects');
    else if (toolbar.show_in_progress_only) parts.push('Status: Projects In Progress');
    else if (toolbar.show_completed_projects) parts.push('Status: All Projects (including completed)');
    else parts.push('Status: Uncompleted Projects');

    const roleLabels: { [key: string]: string } = {
      all: 'Ignore Role (everyone)', worker: 'Where I Am a Worker', follower: 'Where I Am a Follower',
      team_member: 'Viewing For a Team Member',
    };
    parts.push(`My Role Filter: ${roleLabels[toolbar.project_person_filter] || toolbar.project_person_filter}`);
    if (toolbar.project_person_filter === 'team_member' && toolbar.view_team_member_guid) {
      const name = this.team_members?.find(m => m.guid === toolbar.view_team_member_guid)?.display_name || 'a team member';
      parts.push(`Team Member: ${name} (${toolbar.view_team_member_role === 'follower' ? 'Follower' : 'Worker'})`);
    }

    if (this.group_type_change && toolbar.group_by) {
      const groupByLabels: { [key: string]: string } = {
        groups: 'Groups', groups_companies: 'Groups/Companies', groups_contacts: 'Groups/Contacts',
        health: 'Health Status', companies: 'Companies', companies_groups: 'Companies/Groups',
        contacts: 'Contacts', contacts_groups: 'Contacts/Groups', clients: 'Clients', clients_groups: 'Clients/Groups',
      };
      parts.push(`Group By: ${groupByLabels[toolbar.group_by] || toolbar.group_by}`);
    }
    if (typeof toolbar.hide_empty_groups === 'boolean') {
      parts.push(`Empty Groups: ${toolbar.hide_empty_groups ? 'Hidden' : 'Shown'}`);
    }
    return parts;
  };

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }
  projectStatusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Completed', value: 'Completed' },
    { label: 'On Pause', value: 'On Pause' },
    { label: 'Inactive',value: 'Inactive'}
  ];

  // Firm-configurable (Manage Firm > Projects > Manage Health Scoring), so built from the cached config
  // rather than a fixed list like projectStatusOptions - but as a plain field refreshed once (see
  // RefreshHealthLevelOptions), NOT a getter. p-multiSelect's [options] identity-compares on every
  // change detection cycle; a getter that .map()s a new array every tick made it appear to reopen/
  // reset itself on every click, which is why the Health column filter didn't seem to let you click
  // anything - the health_label filter's real bug (no registered match-mode function at all) is fixed
  // separately below.
  healthLevelOptions: { label: string; value: string }[] = [];

  private RefreshHealthLevelOptions() {
    this.healthLevelOptions = (this.projectHealthService.GetCachedConfig()?.levels || []).map(l => ({ label: l.label, value: l.label }));
  }

  /**
   * Column configuration for the column picker
   */
  availableColumns: ColumnConfig[] = [
    //{ key: 'details', label: 'Details', visible: true },
    { key: 'title', label: 'Title', visible: true },
    { key: 'internal_status', label: 'Internal Status', visible: true },
    { key: 'status', label: 'System Status', visible: true },
    { key: 'linked_name', label: 'Linked To', visible: true },
    { key: 'client_year_end_date', label: 'Client Year End Date', visible: true },
    { key: 'progress', label: 'Progress', visible: true },
    { key: 'health', label: 'Health', visible: true },
    { key: 'internal_start_date', label: 'Project Start Date', visible: true },
    { key: 'internal_end_date', label: 'Project End Date', visible: true },
    { key: 'period_start_date', label: 'Period Start Date', visible: false },
    { key: 'period_end_date', label: 'Period End Date', visible: false },
    { key: 'estimated_time', label: 'Estimated Time', visible: true },
    { key: 'time_recorded', label: 'Time Recorded', visible: true },
    { key: 'estimated_time_remaining', label: 'Estimated Time Remaining', visible: true },
    { key: 'time_worked', label: 'Time Worked', visible: true },
    { key: 'completed_date', label: 'Completed Date', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
    { key: 'groups', label: 'Groups', visible: true },
    { key: 'document_request_complete', label: 'Client Document Request Complete', visible: true },
    { key: 'last_client_document_upload', label: 'Last Client Document Upload', visible: true },
    { key: 'client_role', label: 'Client Role', visible: true },
    { key: 'project_leader_name', label: 'Project Leader', visible: true },
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.start_date_iso || changes.end_date_iso) {
      this.GetData();
    }
  }
  @Input() current_start_date: Date;
  @Input() current_end_date: Date;
  dateValue: Date[];
  pendingStartDate: string = '';
  pendingEndDate: string = '';
  readonly maxDate = dayjs().add(5, 'year').format('YYYY-MM-DD');

  get canApplyDates(): boolean {
    if (!this.pendingStartDate || !this.pendingEndDate) return false;
    if (!this.current_start_date || !this.current_end_date) return true;
    return (
      dayjs(this.pendingStartDate).format('YYYY-MM-DD') !== dayjs(this.current_start_date).format('YYYY-MM-DD') ||
      dayjs(this.pendingEndDate).format('YYYY-MM-DD') !== dayjs(this.current_end_date).format('YYYY-MM-DD')
    );
  }

  applyDateFilter() {
    if (!this.canApplyDates) return;
    const start = dayjs(this.pendingStartDate).startOf('day').toDate();
    const end = dayjs(this.pendingEndDate).endOf('day').toDate();
    this.DateChanged([start, end]);
  }

  getTableDescription(): string {
    const parts: string[] = [];

    const projectLabel = this.show_overdue_only ? 'overdue projects' : this.show_in_progress_only ? 'projects in progress' : this.show_completed_projects ? 'all projects (including completed)' : 'open projects';
    let datePart = '';
    if (this.current_start_date && this.current_end_date) {
      const start = dayjs(this.current_start_date).format('MMM D, YYYY');
      const end = dayjs(this.current_end_date).format('MMM D, YYYY');
      datePart = ` from ${start} to ${end}`;
    }
    const forNames: string[] = [];
    if (this.view_client?.display_name) {
      forNames.push(this.view_client.display_name);
    } else if (this.view_company?.name) {
      forNames.push(this.view_company.name);
    }
    if (this.url_mod?.includes('team_member=') && this.team_member?.display_name) {
      forNames.push(this.team_member.display_name);
    }
    const forPart = forNames.length > 0 ? ` for ${forNames.join(' and ')}` : '';
    const viewedTeamMemberName = this.team_members.find(m => m.guid === this.view_team_member_guid)?.display_name;
    const rolePart = this.project_person_filter === 'worker' ? ' where I am a worker'
      : this.project_person_filter === 'follower' ? ' where I am a follower'
      : (this.project_person_filter === 'team_member' && viewedTeamMemberName) ? ` where ${viewedTeamMemberName} is a ${this.view_team_member_role}`
      : '';
    parts.push(`Showing ${projectLabel}${datePart}${forPart}${rolePart}`);

    if (this.quickSearchTerm?.trim()) {
      parts.push(`quick searching for: ${this.quickSearchTerm.trim()}`);
    }

    if (this.groupView) {
      const groupPart = this.hide_empty_groups ? 'grouped by category (empty groups hidden)' : 'grouped by category';
      parts.push(groupPart);
      // Unlike tag-groups (this.groups - the real, complete list, so an empty one still shows a
      // bucket), Companies/Contacts buckets only exist at all for entities with at least one project
      // in the current filter (see UpdateSingleDimensionGroups/UpdateCombinedGroups), so worth calling
      // out explicitly rather than leaving it to look like the rest just don't exist.
      if (this.group_by === 'clients' || this.group_by === 'clients_groups') {
        parts.push('contacts and companies with no projects in this range are hidden');
      } else if (this.group_by === 'companies' || this.group_by === 'companies_groups') {
        parts.push('companies with no projects in this range are hidden');
      } else if (this.group_by === 'contacts' || this.group_by === 'contacts_groups') {
        parts.push('contacts with no projects in this range are hidden');
      }
    } else {
      parts.push('in table view');
    }

    if (this.is_searching) {
      parts.push('a search filter is active');
    }

    return parts.length === 1 ? parts[0] + '.' : parts[0] + ', ' + parts.slice(1).join(', ') + '.';
  }

  first_rule = {
    field: 'Title',
    opr: 'equals',
    value: '',
    type: 'string'
  }

  fields = [
    {
      name: 'Title',
      type: 'string'
    },
    {
      name: 'Description',
      type: 'string'
    },
    {
      name: 'Due Date',
      type: 'date'
    },
    {
      name: 'Completed Date',
      type: 'date'
    },
    {
      name: 'Progress',
      type: 'number'
    },
    {
      name: 'Internal',
      type: 'boolean'
    },
    //         {
    //   name: 'Internal Status',
    //   type: 'category',
    //   options: {

    //   }
    // },
    {
      name: 'Status',
      type: 'category',
      options: {

      }
    },
    {
      name: 'Tags',
      type: 'category',
      options: {

      },
    },
    {
      name: 'Groups',
      type: 'category',
      options: {

      },
    }
  ];
  team_members = [];
  // Options for the Project Leader column filter's p-multiSelect - a plain field recomputed once
  // whenever team_members is (re)loaded (see FormatData), NOT a getter. A getter that builds a new
  // array on every change-detection cycle is what made the Health filter's multiSelect appear
  // unclickable (identity changes on every tick reset the overlay); this avoids that same trap.
  teamMemberFilterOptions: any[] = [];

  private RefreshTeamMemberFilterOptions() {
    this.teamMemberFilterOptions = [{ guid: '', display_name: 'Unassigned' }, ...this.team_members];
  }


  //CSS classes
  @Input() no_margin = false;

  constructor(private modalController: ModalController, private alertController: AlertController, private apiService: ApiService, public globalService: GlobalService, private searchService: SearchService, private projectService: ProjectsService, private filterService: FilterService,private cookieService: CookieService, public projectHealthService: ProjectHealthService, private tablePdfExportService: TablePdfExportService, private tableExcelExportService: TableExcelExportService, private router: Router, private cdr: ChangeDetectorRef, private timeAndBillingService: TimeAndBillingService) {
    dayjs.extend(isBetween);
  }

  // Pipes (e.g. `number`) aren't allowed inside event-binding expressions like (mouseenter)="...",
  // so the tooltip text is formatted here instead of inline in the template. The tooltip renders as
  // HTML (see GlobalService.ShowTooltip), so the reasons are joined with <br> as a bullet list.
  GetHealthTooltipText(health: any): string {
    const reasons = this.projectHealthService.GetHealthReasons(health, this.projectHealthService.GetCachedConfig());
    const reason_list = reasons.map(r => `• ${r}`).join('<br>');
    return `Score: ${Math.round(health.score)} — ${health.level.label}<br>${reason_list}`;
  }


  ngOnInit() {
    if(this.column_picker_enabled)this.initializeColumnVisibility();
    this.RestoreFilterCookies();
    this.GetTeamMember();
    this.LoadViewEntity();
    this.dateValue = new Array(2);
    // Only default to 25 the first time (no saved preference yet) - don't clobber a restored one
    if (this.show_paging && !this.restored_max_per_page) this.max_per_page = 25;
    this.today = dayjs().toDate();
    // ProjectHealthService's own constructor kicks off its config load, so GetCachedConfig() could
    // still be the default here - re-fetch (idempotent) so a firm-customized set of health levels ends
    // up in the Health column filter's options instead of just the defaults.
    this.projectHealthService.GetConfig().then(() => this.RefreshHealthLevelOptions());
    this.RefreshHealthLevelOptions();

    this.CheckHash();
    this.GetData();


    this.filterService.register('tagsMatch', (projectTags: any, selectedTagGuids: string[]) => {

      if (!selectedTagGuids || selectedTagGuids.length === 0) {
        return true;
      }

      if (!projectTags || projectTags.length === 0) {
        return false;
      }

      // Require ALL selected tags to exist on the project
      return selectedTagGuids.every(guid =>
        projectTags.some(t => t.guid === guid)
      );
    });
    this.filterService.register('groupsMatch', (projectGroups: any, selectedGroupGuids: string[]) => {

      if (!selectedGroupGuids || selectedGroupGuids.length === 0) {
        return true;
      }

      if (!projectGroups || projectGroups.length === 0) {
        return false;
      }

      // Require ALL selected tags to exist on the project
      return selectedGroupGuids.every(guid =>
        projectGroups.some(t => t.guid === guid)
      );
    });

    this.filterService.register('projectInternalStatusMatch', (guid: string, selectedGuids: string[]) => {
      if (!selectedGuids || selectedGuids.length === 0) return true;
      return selectedGuids.includes(guid);
    });

    this.filterService.register('projectStatusMatch', (statusLabel: string, selectedLabels: string[]) => {
      if (!selectedLabels || selectedLabels.length === 0) return true;
      return selectedLabels.includes(statusLabel);
    });

    // Neither internal_status nor status_label ever set an explicit matchMode on their
    // <p-columnFilter>, so PrimeNG falls back to the built-in CONTAINS matchMode - which happens to
    // still produce usable results against a plain string field. health_label had no registered
    // matchMode at all, and the filter's own [matchModeOptions] offered "Includes Any"/"Includes All"
    // values ('singleIncludesAny'/'singleIncludesAll') that were never registered anywhere either, so
    // selecting a health value silently filtered against a function that didn't exist. This registers
    // the actual match used by health_label's <p-columnFilter matchMode="healthMatch"> in the template
    // below - a project only ever has one health label, so "includes any of the selected values" is
    // the only sensible semantics (matches projectStatusMatch's shape exactly for the same reason).
    this.filterService.register('healthMatch', (healthLabel: string, selectedLabels: string[]) => {
      if (!selectedLabels || selectedLabels.length === 0) return true;
      return selectedLabels.includes(healthLabel);
    });

    // A project only ever has one Project Leader (there's no separate "Team Member Assigned" column
    // here the way Tasks has one), so this is "Includes Any" only too. '' represents Unassigned (see
    // RefreshTeamMemberFilterOptions), matching however team_leader_guid comes back unassigned (empty
    // string, null, or undefined) rather than requiring an exact '' on the project itself.
    this.filterService.register('projectLeaderMatch', (guid: string, selected: string[]) => {
      if (!selected || selected.length === 0) return true;
      return selected.includes(guid || '');
    });

  }



  async GetTeamMember() {
    let get_guid = '';
    // If url_mod includes a team_member param, extract guid and set this.team_member so the following API call can use it
    if (this.url_mod && typeof this.url_mod === 'string' && this.url_mod.includes('team_member=')) {
      const match = this.url_mod.match(/team_member=([^&'"]+)/);
      if (match && match[1]) {
        const raw = match[1].replace(/['"+]/g, '');
        const guid = decodeURIComponent(raw);
        get_guid = guid;
      }
    }
    else {
      let current_usr = await this.globalService.GetCurrentUser();
      get_guid = current_usr.guid;
    }
    
    let data: any = await this.apiService.get(`team_members/${get_guid}`);
    if (data.success) {
      
      this.team_member = data.data[0];
      //make the object cleaner
      if (!this.team_member.skills) this.team_member.skills = [];
      if (!this.team_member.projects) this.team_member.projects = [];
      if (!this.team_member.tasks) this.team_member.tasks = [];
    }

  }

  async LoadViewEntity() {
    if (!this.url_mod) return;
    const clientMatch = this.url_mod.match(/client=([^&'"]+)/);
    const companyMatch = this.url_mod.match(/company=([^&'"]+)/);
    if (clientMatch) {
      const guid = decodeURIComponent(clientMatch[1].replace(/['"+]/g, ''));
      this.view_client = await this.globalService.GetClient(guid);
    } else if (companyMatch) {
      const guid = decodeURIComponent(companyMatch[1].replace(/['"+]/g, ''));
      this.view_company = await this.globalService.GetCompany(guid);
    }
  }

  ngAfterViewInit(): void {
    this.DeleteExpandedRows();
  }

  async DeleteExpandedRows() {
    setTimeout(() => {
      const stateKey = 'app-projects-table';
      const savedState = localStorage.getItem(stateKey);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.expandedRowKeys) {
          delete parsed.expandedRowKeys;
          localStorage.setItem(stateKey, JSON.stringify(parsed));
        }
      }
    });
  }

  async FormatData(projects: any[] = this.all_projects) {
    if (this.team_members.length == 0) await this.GetTeamMembers();
    this.RefreshTeamMemberFilterOptions();
    if (!projects) return;

    const timeEntries: any[] = await this.timeAndBillingService.GetEntries();

    await Promise.all(projects.map(async project => {
      project.time_worked = this.CalculateProjectTimeWorked(project.guid, timeEntries);
      project.time_recorded = project.time_worked;
      project.estimated_time_remaining = (Number(project.estimated_time) || 0) - (Number(project.time_recorded) || 0);
      if(!project.all_team_members) {
      }
      if (project.client_guid) {
        project.client = await this.globalService.GetClient(project.client_guid);
        project.linked_name = project.client.display_name;
      }
      else if (project.company_guid) {
        project.company = await this.globalService.GetCompany(project.company_guid);
        project.linked_name = project.company.name;
      }
      // Only companies have a year end - blank for a contact
      project.client_year_end_date = !project.client_guid && project.company?.year_end_date ? new Date(project.company.year_end_date) : null;
      // Mirrors the exact precedence the Status column badge uses (see table.component.html's
      // 'status' *ngSwitchCase) - status 21 (Awaiting Missing Info) labels itself even when paused,
      // since paused is just the general "something else stopped this project" case. Without this
      // branch, a status-21 project got status_label = undefined, which doesn't match any option in
      // the Status column filter and silently hid the whole row rather than just its health badge.
      if (project.paused && project.status == 21) project.status_label = 'Awaiting Missing Info';
      else if (project.paused) project.status_label = 'On Pause';
      else if (project.status === 1) project.status_label = 'Completed';
      else if(project.status === -1) project.status_label = 'Inactive';
      else if (project.status === 0) project.status_label = 'Active';
      else if (project.status == 21) project.status_label = 'Awaiting Missing Info';
      // Precomputed the same way status_label is, so the Health column's filter (health_label) and
      // sort (health_score) can both work against a plain field like any other column - null for
      // projects Evaluate() has no score for (not active/not started). PrimeNG's default sort
      // comparator treats null as lowest, so those rows land first ascending / last descending,
      // same as any other column with missing data.
      const health = this.projectHealthService.Evaluate(project);
      // Kept on the project so the Health column reads it instead of re-scoring every row on every change detection pass
      project.health = health;
      project.health_label = health ? health.level.label : null;
      project.health_score = health ? health.score : null;
      if (project.internal_start_date && !(project.internal_start_date instanceof Date)) project.internal_start_date = new Date(project.internal_start_date);
      if (project.internal_end_date && !(project.internal_end_date instanceof Date)) project.internal_end_date = new Date(project.internal_end_date);
      if (project.period_start_date && !(project.period_start_date instanceof Date)) project.period_start_date = new Date(project.period_start_date);
      if (project.period_end_date && !(project.period_end_date instanceof Date)) project.period_end_date = new Date(project.period_end_date);
      if (project.completed_date && !(project.completed_date instanceof Date)) project.completed_date = new Date(project.completed_date);
      if (project.last_client_document_upload && !(project.last_client_document_upload instanceof Date)) project.last_client_document_upload = new Date(this.globalService.DateToUTCFormat(project.last_client_document_upload)); // stored in UTC without a Z
      if (project.document_request_complete && !(project.document_request_complete instanceof Date)) project.document_request_complete = new Date(this.globalService.DateToUTCFormat(project.document_request_complete)); // stored in UTC without a Z
      project.latest_comment_date = [
        this.globalService.DateToUTCFormat(project.project_last_comment_date),
        this.globalService.DateToUTCFormat(project.task_last_comment_date),
        this.globalService.DateToUTCFormat(project.step_last_comment_date)
      ].filter(d => !!d).reduce((max, d) => (!max || d > max ? d : max), null);

      project.project_leader_name = this.GetAssignedTeamMember(project.team_leader_guid);
    }));
  }


  // Sums every time_and_billing entry (logged against the project directly, or against one of its
  // tasks/actions) that carries this project's guid - entries created from the project/task/step
  // details "Add Time Entry" flows all stamp project_guid, so a single project_guid match covers all
  // three levels. Returns null (renders as "-") rather than 0 when nothing has been logged yet.
  private CalculateProjectTimeWorked(project_guid: string, timeEntries: any[]): number | null {
    if (!project_guid || !timeEntries?.length) return null;
    let totalMinutes = 0;
    let found = false;
    for (const entry of timeEntries) {
      const entryProjectGuid = entry.project_guid || entry.project?.guid;
      if (entryProjectGuid !== project_guid) continue;
      if (!entry.start_date || !entry.end_date) continue;
      found = true;
      const diff = this.globalService.GetTimeDifferenceAsObject(entry.start_date, entry.end_date);
      totalMinutes += (diff.hours * 60) + diff.minutes;
    }
    return found ? totalMinutes : null;
  }

  FormatMinutesToDuration(minutes: number): string {
    const total = Number(minutes) || 0;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return `${hours}h ${mins}m`;
  }

  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    if (data.success) {
      this.team_members = data.data;
    }
    if (!this.team_members) this.team_members = [];
  }


   GetAssignedTeamMember(guid) {
    if (!guid) return 'Unassigned';
    if (guid == '') return 'Unassigned';
    if (this.team_members.length == 0) {
      return "";
    }

    const member = this.team_members.find(m => m.guid === guid);
    return member ? member.display_name : 'Unassigned';
  }


  async presentDetailsModal(project) {

    await this.CreateFromFakeProject(project);

    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: {
        guid: project.guid
      },
      backdropDismiss: false
    })
    modal.onDidDismiss()
      .then((data: any) => {
        
        
        if (data.data) {
          if (data.data.deleted) this.RemoveProjectRow(project.guid);
          else if (data.data.refresh) this.RefreshProjectInPlace(project.guid);
          else this.UpdateGroupsCountKeepingPage();

        }
        //

      });
    return await modal.present()
  }

  async OpenLinkedEntity(project: any, event?: Event) {
    if (event) event.stopPropagation();
    let modal: HTMLIonModalElement;
    if (project.client_guid) {
      modal = await this.modalController.create({
        component: ClientDetailsPage,
        cssClass: 'large-modal',
        componentProps: { client: { guid: project.client_guid } },
        backdropDismiss: false
      });
    } else if (project.company_guid) {
      modal = await this.modalController.create({
        component: CompanyDetailsPage,
        cssClass: 'large-modal',
        componentProps: { company: { guid: project.company_guid } },
        backdropDismiss: false
      });
    } else {
      return;
    }
    return await modal.present();
  }

  async OpenProjectLeaderDetails(project: any, event?: Event) {
    if (event) event.stopPropagation();
    if (!project.team_leader_guid) return;
    const modal = await this.modalController.create({
      component: TeamMemberDetailsPage,
      cssClass: 'large-modal',
      componentProps: { team_member: { guid: project.team_leader_guid } },
      backdropDismiss: false
    });
    return await modal.present();
  }

  async OpenQuickComment(item: any) {
    await this.CreateFromFakeProject(item);
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.guid, activeIndex: 'Comments' },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.refresh) this.RefreshProjectInPlace(item.guid);
    });
    return await modal.present();
  }

  // After a project's window closes with changes: reloads just that project and updates its row, instead of the whole list
  async RefreshProjectInPlace(guid: string) {
    const existing = (this.all_projects || []).find(p => p.guid === guid);
    if (!existing) return; // not one of the rows shown
    try {
      const data: any = await this.apiService.get(`projects/${guid}`);
      const fresh = data?.success ? data.data?.[0] : null;
      if (!fresh) throw new Error('project not found');
      // The single project carries its tasks and payments too; the list rows don't, so only a row that already holds its tasks
      // (an expanded one) takes them
      const { tasks, payments, creator, ...rest } = fresh;
      Object.assign(existing, rest);
      if (Array.isArray(existing.tasks) && Array.isArray(tasks)) existing.tasks = tasks;
      await this.FormatData([existing]);
      this.UpdateGroupsCountKeepingPage();
      this.projectModified.emit({ data: existing });
    } catch (e) {
      // Couldn't update just that row - fall back to reloading the list
      this.GetData();
    }
  }

  // A deleted project leaves the table without reloading it
  private RemoveProjectRow(guid: string) {
    this.all_projects = (this.all_projects || []).filter(p => p.guid !== guid);
    this.projects = (this.projects || []).filter(p => p.guid !== guid);
    this.UpdateGroupsCountKeepingPage();
  }

  // UpdateGroupsCount rebuilds what the tables show, which sends the paginators back to page 1 - put them back
  private UpdateGroupsCountKeepingPage() {
    const pages = (this.tables?.toArray() || []).map((t: any) => t.first);
    this.UpdateGroupsCount();
    setTimeout(() => {
      (this.tables?.toArray() || []).forEach((t: any, i: number) => {
        if (pages[i] != null && t.first !== pages[i]) t.first = pages[i];
      });
    });
  }

  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'projects/comments', guid: item.guid, title: 'Add Comment to Project' }
    });
    modal.onDidDismiss().then((result: any) => {
      if (result?.data?.success) item.project_comment_count = (item.project_comment_count ?? 0) + 1;
    });
    return await modal.present();
  }

  GetCommentTooltip(item: any): string {
    if (!item.latest_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(item.latest_comment_date).format('MMM D, YYYY h:mm A')}`;
  }

  async GetProjectDetails(project, user_open) {
    if (user_open) project.loaded_details = false;
    await this.CreateFromFakeProject(project);

    if (user_open) project.loaded_details = false;
    let data: any = await this.apiService.get(`projects/${project.guid}`);
    if (data.success) {
      
      project.tasks = data.data[0].tasks;
      project.progress = data.data[0].progress;
      project.estimated_time = data.data[0].estimated_time;
      project.estimated_time_remaining = data.data[0].estimated_time_remaining;
      if (user_open) project.loaded_details = true;
    }
  }

  async taskModified(eventData: { data: any }) {
    
    //We are going to find the task, and update it in the task array
    let returned_task_data = eventData.data;
    let index = this.projects.findIndex(x => x.guid === returned_task_data.project_guid);
    let project = this.projects[index];
    let data: any = await this.apiService.get(`projects/${project.guid}`);
    project.progress = data.data[0].progress;
    project.status = data.data[0].status;
    project.estimated_time = data.data[0].estimated_time;
    project.estimated_time_remaining = data.data[0].estimated_time_remaining;
    for (let i = 0; i < data.data[0].tasks.length; i++) {
      this.UpdateTaskInfo(project,project.tasks[i], data.data[0].tasks[i]);
    }

  }

  async UpdateTaskInfo(project,task, task_data) {
    task.progress = task_data.progress;
    task.status = task_data.status;
    task.project_guid = task_data.project_guid;
    task.completed_date = task_data.completed_date;
    if(!this.IsPreviousTaskCompleted(project,task)) task.status = TaskStatus.awaiting_previous_task;
    
  }

  IsPreviousTaskCompleted(project, task) {
    if (!project || !project.tasks || project.tasks.length === 0) {
      return true;
    }
    task.has_checked_status = true;
    if (!project.force_order) {
      return true;
    }

    const currentTaskIndex = project.tasks.findIndex(t => t.guid === task.guid);
    if (currentTaskIndex <= 0) {
      return true;
    }

    const previousTask = project.tasks[currentTaskIndex - 1];
    if (previousTask.status == TaskStatus.completed) {
      return true;
    }

    if (previousTask.status != TaskStatus.completed) {
      return false;
    }
    else return true;
  }


  RowChange($event) {
    this.selectedProjectEvent.emit(this.selectedProjects);
  }

  DateChanged(value) {
    
    // Only update and fetch data if dates have changed
    const newStart = value[0];
    const newEnd = value[1];
    if (
      (!this.current_start_date || !this.current_end_date) ||
      (newStart && newEnd && (
        this.current_start_date.getTime() !== newStart.getTime() ||
        this.current_end_date.getTime() !== newEnd.getTime()
      ))
    ) {
      this.current_start_date = newStart;
      this.current_end_date = newEnd;
      this.dateValue[0] = this.current_start_date;
      this.dateValue[1] = this.current_end_date;

      if (this.loaded_projects) this.GetData();
    }
  }

  FormatDates() {
    if (this.start_date_iso && this.end_date_iso) {
      this.current_start_date = dayjs(this.start_date_iso).toDate();
      this.current_end_date = dayjs(this.end_date_iso).toDate();
    } else {
      if (!this.current_start_date) this.current_start_date = dayjs().subtract(6,'month').startOf('month').startOf('day').toDate();
      if (!this.current_end_date) this.current_end_date = dayjs().add(6,'month').endOf('month').endOf('day').toDate();
    }
    this.dateValue[0] = this.current_start_date;
    this.dateValue[1] = this.current_end_date;
    this.pendingStartDate = dayjs(this.current_start_date).format('YYYY-MM-DD');
    this.pendingEndDate = dayjs(this.current_end_date).format('YYYY-MM-DD');
  }

  /**
   * Filters a chunk of freshly-fetched projects according to the non-inclusive date rules.
   * Mirrors the filtering previously applied to the full result set in GetData().
   */
  private FilterChunkByDateType(chunk_projects: any[]): any[] {
    if (this.inclusive_dates || this.date_filter_type === 'happenning') return chunk_projects;
    if (!this.current_start_date || !this.current_end_date) return chunk_projects;

    const startIso = this.current_start_date.toISOString();
    const endIso = this.current_end_date.toISOString();
    return chunk_projects.filter(project => {
      const dateField = this.date_filter_type === 'start' ? project.internal_start_date : project.internal_end_date;
      if (!dateField) return true;
      return dayjs(dateField).isBetween(startIso, endIso, null, '[]');
    });
  }

  async GetData() {
    // Bump the generation so an in-flight chunked load from a previous call stops updating the view
    this._loadGeneration++;
    const generation = this._loadGeneration;

    this.loaded_projects = false;
    this.backgroundLoading = false;
    this.FormatDates();
    if (this.clients_roles.length === 0) {
      const roleData: any = await this.apiService.GetRoles();
      if (roleData && roleData.success) this.clients_roles = roleData.data || [];
    }
    let start_date = this.current_start_date.toISOString();
    let end_date = this.current_end_date.toISOString();

    // Reset for a fresh load; projects/tasks are (re)built up chunk by chunk below
    this.all_projects = [];
    this.projects = [];

    await this.GetGroups();
    await this.GetTags();
    await this.GetProjectInternalStatuses();

    // Load projects (including generated future/recurring ones) one month at a time so we
    // never hold a whole multi-year range of generated projects in memory simultaneously,
    // and the table can start showing results before the entire range has finished loading.
    await this.projectService.GetProjectsChunked(
      start_date,
      end_date,
      this.url_mod,
      async (chunk_projects, meta) => {
        if (generation !== this._loadGeneration) return;

        const filtered_chunk = this.FilterChunkByDateType(chunk_projects);
        if (filtered_chunk.length) {
          await this.FormatData(filtered_chunk);
          if (generation !== this._loadGeneration) return;
          this.all_projects.push(...filtered_chunk);
        }

        if (this.is_searching && this.current_search_query) {
          const fieldMappingConfig = this.searchService.createProjectFieldMapping();
          this.projects = this.searchService.filterDataByQuery(this.all_projects, this.current_search_query, fieldMappingConfig);
        } else {
          this.projects = [...this.all_projects];
        }

        this.UpdateGroupsCount();
        this.loaded_projects = true;
        this.backgroundLoading = !meta.is_last;
      },
      () => generation !== this._loadGeneration
    );

    if (generation !== this._loadGeneration) return;
    this.backgroundLoading = false;
    this.loaded_projects = true;
  }

  
  async CheckHash() {
    if (!this.allow_hash_set) return;
    if (window.location.hash.replace('#', '')) {
      let hash_check = window.location.hash.replace('#', '');
      if (hash_check.length > 1) {
        let project = {
          guid: hash_check,
          is_fake: false
        }
        
        this.presentDetailsModal(project);
      }
    }
  }



  async GetGroups() {
    let data: any = await this.apiService.GetGroups('projects');
    this.groups = data.data;
    if (!this.groups) this.groups = [];
    this.UpdateGroupsCount();
  }

  async GetTags() {
    let data: any = await this.apiService.GetTags('projects');
    this.tags = data.data;
    if (!this.tags) this.tags = [];
  }



  async presentCreateModal() {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      componentProps: {
        preselected_clients: this.create_client ? [{ ...this.create_client, is_contact: true }] : [],
        preselected_companies: this.create_company ? [this.create_company] : []
      },
      backdropDismiss: false
    })
    // Project create covers this table, but every keystroke in it would still re-check every row here (each change
    // detection pass runs across the whole app). Pause this table while it's open and catch it up once it closes.
    this.cdr.detach();
    modal.onDidDismiss().then((data: any) => {
      this.cdr.reattach();
      this.cdr.detectChanges();
      if (data.data) {
        this.GetData();
      }
    });
    return await modal.present()
  }

  async presentAssignModal() {
    
    const modal = await this.modalController.create({
      component: AssignPage,
      cssClass: 'large-modal',
      backdropDismiss: false
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetData();
      }
    });
    return await modal.present()
  }

  async OpenSearchModal() {
    const modal = await this.modalController.create({
      component: SearchComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        start_date: this.current_start_date,
        end_date: this.current_end_date,
        fields: this.fields,
        type: 'projects',
        show_date_range: true,
        get_groups: true,
        get_tags: true,
        get_status: true,
        get_internal_status: true,
        get_team_members: false,
        first_rule: this.first_rule,
      }
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        
        this.current_start_date = data.data.start_date;
        this.current_end_date = data.data.end_date;
        this.dateValue[0] = this.current_start_date;
        this.dateValue[1] = this.current_end_date;
        this.SearchProjects(data.data.rules);
      }
    });
    return await modal.present()
  }

  async SearchProjects(query: any) {
    await this.GetData();
    this.is_searching = true;
    this.current_search_query = query;
    

    // Use the search service to filter projects
    const fieldMappingConfig = this.searchService.createProjectFieldMapping();
    const filteredProjects = this.searchService.filterDataByQuery(this.all_projects, query, fieldMappingConfig);

    this.projects = filteredProjects;
    this.UpdateGroupsCount();
  }

  /**
   * Clears the current search and shows all projects
   */
  clearSearch() {
    this.is_searching = false;
    this.current_search_query = null;
    this.projects = [...this.all_projects];
    // Restore view from cookie
    this.RestoreFilterCookies();
    this.UpdateGroupsCount();
  }

  //Project Template
  async OpenManageTemplatesModal() {
    const modal = await this.modalController.create({
      component: TemplateBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_template_selection: true,
        template_select_mode: false,
        template_type: 'project',
        edit_select_mode: true
      },
    })
    modal.onDidDismiss().then((data: any) => {
      
      
    });
    return await modal.present()
  }

  async OpenManageProjectSchedule() {
    const modal = await this.modalController.create({
      component: SchedulePage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {

      },
    })
    modal.onDidDismiss().then((data: any) => {
      if(data.data) {
        this.GetData();
      }
    });
    return await modal.present()
  }

  getClientRoleName(role_guid: string): string {
    if (!role_guid) return '';
    const role = this.clients_roles.find(r => r.guid === role_guid);
    return role ? role.title : '';
  }

  async GetProjectInternalStatuses() {
    const data: any = await this.apiService.get('tags/projects_internal_status');
    if (data.success) this.projectInternalStatuses = data.data || [];
    if(!this.projectInternalStatuses) this.projectInternalStatuses = [];
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.projectInternalStatuses || this.projectInternalStatuses.length === 0) return null;
    return this.projectInternalStatuses.find(s => s.guid === guid) || null;
  }

  async ProjectInternalStatusChange(project) {
    await this.CreateFromFakeProject(project);
    //Update the project status
    this.apiService.put(`projects/${project.guid}`, { internal_status: project.internal_status });
  }

  async OpenManageTags() {
    const modal = await this.modalController.create({
      component: ManageComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'tags',
        url: 'projects',
        local_only: true,
        title: "Manage Tags",
        name: "Tags",
      },
    })
    modal.onDidDismiss().then((data: any) => {

    });
    return await modal.present()
  }

  async OpenManageProjectInternalStatuses() {
    const modal = await this.modalController.create({
      component: ManageComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'tags',
        url: 'projects_internal_status',
        title: 'Manage Project Internal Statuses',
        name: 'Internal Statuses',
      },
    });
    modal.onDidDismiss().then(async () => {
      await this.GetProjectInternalStatuses();
    });
    return await modal.present();
  }

  async OpenManageGroups() {
    const modal = await this.modalController.create({
      component: ManagerComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'groups',
        url: 'projects',
        local_only: true,
        title: "Manage Groups",
        name: "Groups",
      },
    })
    modal.onDidDismiss().then((data: any) => {
      this.GetGroups();
    });
    return await modal.present()
  }

  //Group filtering
  UpdateGroupsCount() {
    
    this.non_grouped_objects = [];
    if (!this.projects) return;
    for (const project of this.GetFilteredProjects(this.projects, null, false)) {
      if (!project.groups || project.groups.length === 0) {
        if (!this.show_all_projects) continue;
        this.non_grouped_objects.push(project);
      }
    }
    this.UpdateHealthGroups();
    this.UpdateSingleDimensionGroups(this.companyGroups, 'companies');
    this.UpdateSingleDimensionGroups(this.contactGroups, 'contacts');
    this.UpdateSingleDimensionGroups(this.clientGroups, 'clients');
    this.UpdateCombinedGroups(this.groupsCompaniesGroups, 'groups', 'companies');
    this.UpdateCombinedGroups(this.companiesGroupsGroups, 'companies', 'groups');
    this.UpdateCombinedGroups(this.groupsContactsGroups, 'groups', 'contacts');
    this.UpdateCombinedGroups(this.contactsGroupsGroups, 'contacts', 'groups');
    this.UpdateCombinedGroups(this.clientsGroupsGroups, 'clients', 'groups');
    if (!this.groups || !this.projects) {

      return;
    }
    for (const group of this.groups) {
      group.objects = [];
      for (const project of this.GetFilteredProjects(this.projects, group, false)) {
        if (!this.show_all_projects) continue;
        if (project.groups && Array.isArray(project.groups)) {
          if (project.groups.some(g => g.guid === group.guid)) {
            group.objects.push(project);
          }
        }
      }
      group.projects = this.GetFilteredProjects(this.projects, group, false);
    }
    this.non_grouped_projects = this.GetFilteredProjects(this.projects, null, true);

    //Loop through each group and if they group has a parent_guid then we need to add the child count to the parent count
    for (const group of this.groups) {
      if (group.parent_guid) {
        const parent_group = this.groups.find(g => g.guid === group.parent_guid);
        if (parent_group) {
          for (const obj of group.objects) {
            if (!parent_group.objects.some(o => o.guid === obj.guid)) {
              parent_group.objects.push(obj);
            }
          }
        }
      }
    }
  }

  // Builds (once) and refreshes the "group by health" bucket list used when group_type_change lets
  // the user switch Group View's grouping away from tags - one bucket per configured health band,
  // plus a trailing "No Health Score" bucket for projects Evaluate() can't score (not active/not
  // started/paused). Buckets are built once and reused afterwards (not reassigned) purely so
  // .expanded survives future recomputes, mirroring how this.groups is never reassigned wholesale
  // in UpdateGroupsCount above - only .objects/.projects are refreshed here.
  private UpdateHealthGroups() {
    if (!this.projects) return;
    if (this.healthGroups.length === 0) {
      const levels = this.projectHealthService.GetCachedConfig()?.levels || [];
      // guid feeds the <p-table>'s [id], so it's slugified - a firm-customized health label could
      // otherwise contain spaces/punctuation that aren't valid in an HTML id.
      this.healthGroups = levels.map((level, i) => ({
        guid: 'health_' + i + '_' + level.label.replace(/[^a-zA-Z0-9]+/g, '_'), title: level.label, colour: level.colour,
        parent_guid: null, objects: [] as any[], projects: [] as any[], expanded: false,
      }));
      this.healthGroups.push({
        guid: 'health_none', title: 'Project that haven\'t started yet', colour: '#adb5bd',
        parent_guid: null, objects: [] as any[], projects: [] as any[], expanded: false,
      });
    }
    const no_health_bucket = this.healthGroups.find(b => b.guid === 'health_none');
    for (const bucket of this.healthGroups) bucket.objects = [];
    // group=null, showNoGroup=false resolves to "every project passing the toggle/search filters,
    // regardless of tag-group membership" (see HideRowLogic) - exactly what health grouping needs,
    // since it's a completely separate axis from tag grouping.
    for (const project of this.GetFilteredProjects(this.projects, null, false)) {
      const label = project.health_label; // worked out once per load (see project.health)
      const bucket = (label && this.healthGroups.find(b => b.title === label)) || no_health_bucket;
      bucket.objects.push(project);
    }
    for (const bucket of this.healthGroups) bucket.projects = bucket.objects;
  }

  // Which bucket(s) a project belongs to for a given grouping dimension - 'groups' can return more
  // than one (a project can carry several tag-groups, same multi-membership as the plain "Groups"
  // mode above) and always matches (falling back to "No Group"). 'companies'/'contacts' return at
  // most one, and return none at all for a project that isn't linked to that entity type - see
  // ProjectMatchesDimension, which is what actually keeps a contact-only project out of every
  // Companies-flavoured view (and a company-only project out of every Contacts-flavoured view)
  // rather than landing in a "No Company"/"No Contact" catch-all.
  private GetDimensionKeys(project: any, dimension: 'groups' | 'companies' | 'contacts' | 'clients'): { guid: string; title: string; colour: string }[] {
    if (dimension === 'groups') {
      if (project.groups && project.groups.length > 0) {
        return project.groups.map((g: any) => ({ guid: 'group_' + g.guid, title: g.title, colour: g.colour }));
      }
      return [{ guid: 'group_none', title: 'No Group', colour: '#adb5bd' }];
    }
    if (dimension === 'companies') {
      if (!project.company_guid) return [];
      return [{ guid: 'company_' + project.company_guid, title: project.company?.name || project.linked_name || 'Company', colour: '#e9ecef' }];
    }
    if (dimension === 'clients') {
      // A project is for a contact or a company (or, rarely, both) - they sit side by side in one list
      const keys: { guid: string; title: string; colour: string }[] = [];
      if (project.company_guid) keys.push({ guid: 'company_' + project.company_guid, title: project.company?.name || project.linked_name || 'Company', colour: '#e9ecef' });
      if (project.client_guid) keys.push({ guid: 'contact_' + project.client_guid, title: project.client?.display_name || project.linked_name || 'Contact', colour: '#e9ecef' });
      return keys;
    }
    // contacts
    if (!project.client_guid) return [];
    return [{ guid: 'contact_' + project.client_guid, title: project.client?.display_name || project.linked_name || 'Contact', colour: '#e9ecef' }];
  }

  // Whether a project belongs in a view built around this dimension at all - 'groups' always does
  // (a project with no tag-groups still shows, under "No Group"); 'companies'/'contacts' only include
  // projects actually linked to that entity type, per the "don't show contact projects when grouping
  // by companies" (and vice versa) behaviour.
  private ProjectMatchesDimension(project: any, dimension: 'groups' | 'companies' | 'contacts' | 'clients'): boolean {
    if (dimension === 'clients') return !!project.company_guid || !!project.client_guid;
    if (dimension === 'companies') return !!project.company_guid;
    if (dimension === 'contacts') return !!project.client_guid;
    return true;
  }

  // Finds the existing bucket with this guid in `store`, or lazily creates one - reusing (rather than
  // rebuilding) bucket objects is what lets .expanded survive future recomputes, same reasoning as
  // UpdateHealthGroups. title/colour are refreshed in place in case a group got renamed/recoloured.
  private GetOrCreateBucket(store: any[], key: { guid: string; title: string; colour: string }, parent_guid: string | null): any {
    let bucket = store.find(b => b.guid === key.guid);
    if (!bucket) {
      bucket = { guid: key.guid, title: key.title, colour: key.colour, parent_guid, objects: [], projects: [], expanded: false };
      store.push(bucket);
    } else {
      bucket.title = key.title;
      bucket.colour = key.colour;
    }
    return bucket;
  }

  // "Companies"/"Contacts" modes - one bucket per company or contact currently represented among the
  // filtered projects, discovered on the fly since (unlike health bands or tag-groups) there's no
  // separate fixed list to seed buckets from up front. Sorted alphabetically by title, and - since
  // GetDimensionKeys/ProjectMatchesDimension exclude the "wrong" entity type entirely - a Companies
  // view never shows a contact-only project (and a Contacts view never shows a company-only one).
  private UpdateSingleDimensionGroups(store: any[], dimension: 'companies' | 'contacts' | 'clients') {
    if (!this.projects) return;
    for (const bucket of store) bucket.objects = [];
    for (const project of this.GetFilteredProjects(this.projects, null, false)) {
      if (!this.ProjectMatchesDimension(project, dimension)) continue;
      for (const key of this.GetDimensionKeys(project, dimension)) {
        this.GetOrCreateBucket(store, key, null).objects.push(project);
      }
    }
    for (const bucket of store) bucket.projects = bucket.objects;
    store.sort((a, b) => a.title.localeCompare(b.title));
  }

  // Two-level grouping ("Groups/Companies", "Companies/Groups", "Groups/Contacts", "Contacts/Groups")
  // - outer buckets from outerDim, each with its own nested inner buckets from innerDim
  // (guid-namespaced under the outer bucket so e.g. the same company under two different groups gets
  // two distinct, independently-expandable buckets). Reuses the exact same recursive rendering the
  // plain tag-group hierarchy already uses (projectGroupItem walks parent_guid), so no template
  // changes were needed for nesting itself. A project is skipped entirely (not just left out of the
  // inner buckets) if it doesn't match whichever of outerDim/innerDim is 'companies' or 'contacts',
  // so an outer bucket's count always matches what its inner buckets actually add up to.
  private UpdateCombinedGroups(store: any[], outerDim: 'groups' | 'companies' | 'contacts' | 'clients', innerDim: 'groups' | 'companies' | 'contacts' | 'clients') {
    if (!this.projects) return;
    for (const bucket of store) bucket.objects = [];
    for (const project of this.GetFilteredProjects(this.projects, null, false)) {
      if (!this.ProjectMatchesDimension(project, outerDim) || !this.ProjectMatchesDimension(project, innerDim)) continue;
      for (const outerKey of this.GetDimensionKeys(project, outerDim)) {
        const outerBucket = this.GetOrCreateBucket(store, outerKey, null);
        outerBucket.objects.push(project);
        for (const innerKey of this.GetDimensionKeys(project, innerDim)) {
          const compoundKey = { guid: outerKey.guid + '__' + innerKey.guid, title: innerKey.title, colour: innerKey.colour };
          const innerBucket = this.GetOrCreateBucket(store, compoundKey, outerBucket.guid);
          innerBucket.objects.push(project);
        }
      }
    }
    // Outer buckets deliberately get an empty .projects (used by projectTable's <p-table>, as opposed
    // to .objects which is just the count badge) - every one of their projects is already fully
    // accounted for under a nested inner bucket (real inner groups, or "No Group" for ungrouped ones),
    // so also rendering the outer bucket's own flat table would just show every project a second time.
    // This only applies here, not to genuine tag-group parents in plain "Groups" mode (this.groups),
    // where a project actually can belong directly to a parent group independent of its subgroups.
    for (const bucket of store) bucket.projects = bucket.parent_guid ? bucket.objects : [];
    // A single sort of the flat array by title alphabetizes both levels at once - top-level buckets
    // end up in alphabetical order relative to each other, and each parent's children end up in
    // alphabetical order among themselves, since the template's *ngFor for each level just filters
    // this same array by parent_guid without otherwise reordering it.
    store.sort((a, b) => a.title.localeCompare(b.title));
  }

  ProjectInGroup(project, group) {

    if (!project.groups || project.groups.length == 0) return false;
    for (const g of project.groups) {

      if (g.guid === group.guid) {
        return true;
      }
    }
    return false;
  }


  // Worker = project_team_members.role 2 ("Worker") or 3 ("Worker (Alt)"); Follower = role 4. Checked
  // explicitly against the project's team_members list - an all_team_members project has no explicit
  // role for anyone, so it never matches Worker or Follower, only the "All" filter.
  IsCurrentUserInProjectRole(project: any, role: 'worker' | 'follower'): boolean {
    const my_guid = this.globalService.GetCurrentUser()?.guid;
    if (!my_guid) return false;
    return this.IsTeamMemberInProjectRole(project, my_guid, role);
  }

  // Same check as IsCurrentUserInProjectRole, but for an arbitrary team member guid instead of
  // always the current user - powers the "View for a Team Member" picker (see
  // ToggleTeamMemberPicker/OnTeamMemberFilterChange).
  IsTeamMemberInProjectRole(project: any, team_member_guid: string, role: 'worker' | 'follower'): boolean {
    if (!team_member_guid) return false;
    if (project.all_team_members) return false;
    const tm = (project.team_members || []).find((t: any) => t.guid === team_member_guid);
    if (!tm) return false;
    if (role === 'worker') return tm.role == 2 || tm.role == 3;
    return tm.role == 4;
  }

  HideRowLogic(project, group, showNoGroup) {
    if (this.project_person_filter === 'team_member') {
      if (!this.IsTeamMemberInProjectRole(project, this.view_team_member_guid, this.view_team_member_role)) return false;
    } else if (this.project_person_filter !== 'all' && !this.IsCurrentUserInProjectRole(project, this.project_person_filter)) {
      return false;
    }
    if (this.show_overdue_only) {
      if (project.status == 1) return false;
      if (!project.internal_end_date || !dayjs(project.internal_end_date).isBefore(dayjs(), 'day')) return false;
    } else if (this.show_in_progress_only) {
      if (project.status == 1 || !(project.progress > 0)) return false;
    } else if (!this.show_completed_projects) {
      if (project.status == 1) return false;
    }
    // if (this.tasks_with_my_skills) {
    //   //Loop through the prorject and see if any tasks have my skills
    //   let has_task_with_my_skills = false;
    //   if (!this.team_member || !this.team_member.skills || this.team_member.skills.length == 0) return false;
    //   if (!project.tasks) return false;

    //   for (const task of project.tasks) {
    //     if (!task.skills || task.skills.length == 0) continue;
    //     for (const skill of task.skills) {
    //       if (!skill || !skill.guid) continue;
    //       if (this.team_member.skills && this.team_member.skills.some((ms: any) => ms && ms.guid === skill.guid)) {
    //         has_task_with_my_skills = true;
    //         break;
    //       }
    //     }
    //     if (has_task_with_my_skills) break;
    //   }
    //   if (!has_task_with_my_skills) return false;
    // }
    // If not in group view, ignore group filtering
    if (!this.groupView) {
      return (this.show_all_projects);
    }

    if (showNoGroup) {
      return (!project.groups || project.groups.length == 0) && (this.show_all_projects);
    }
    else if (!group) {
      return (this.show_all_projects);
    }
    else {
      return this.ProjectInGroup(project, group) && (this.show_all_projects);
    }
  }

  // Opens/closes the Team Member/Role picker next to the My Role Filter buttons. Doesn't change
  // project_person_filter by itself - that only switches to 'team_member' once a team member is
  // actually picked (see OnTeamMemberFilterChange), so closing the panel without picking anyone
  // leaves whatever filter was already active untouched.
  ToggleTeamMemberPicker() {
    this.show_team_member_picker = !this.show_team_member_picker;
  }

  // Fired when either dropdown in the Team Member picker changes. Switches project_person_filter
  // to 'team_member' once a team member is selected, and back to 'all' if the selection is cleared
  // (rather than leaving the table showing zero rows with nothing selected).
  OnTeamMemberFilterChange() {
    this.project_person_filter = this.view_team_member_guid ? 'team_member' : 'all';
    this.UpdateGroupsCount();
    this.UpdateFilterCookies();
  }

  GetFilteredProjects(projects: any[] | null | undefined, group: any, showNoGroup: boolean): any[] {
    const list = projects ?? [];
    let to_return = list.filter(c =>
      this.HideRowLogic(c, group, showNoGroup) &&
      this.MatchesQuickSearch(c) &&
      !c.filter_out_group &&
      !c.filter_out_tag &&
      !c.filter_out_team_member
    );
    return to_return
  }

  // Quick search (see companies/table's onQuickSearchChange/MatchesQuickSearch for the pattern this
  // mirrors) - searches project title, who it's linked to, every assigned team member's name, the
  // project leader, description, the internal status label, and the plain-text health label
  // (health_label is precomputed in FormatData, same field the Health column's filter/sort use, so
  // this always matches what's shown on the badge). Routed through GetFilteredProjects, the single
  // place every grouped/non-grouped/count pass already filters through, so this applies everywhere
  // without needing to be threaded into each call site separately. Debounced 300ms via
  // quickSearchDebounceTimer so filtering doesn't recompute on every keystroke - quickSearchInput
  // tracks the raw typed value for instant display, quickSearchPending drives the "Searching..."
  // indicator while quickSearchTerm (the actually-applied value) is still catching up.
  quickSearchTerm = '';
  quickSearchInput = '';
  quickSearchPending = false;
  private quickSearchDebounceTimer: any;

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term;
    this.quickSearchPending = true;
    clearTimeout(this.quickSearchDebounceTimer);
    this.quickSearchDebounceTimer = setTimeout(() => {
      this.quickSearchTerm = term;
      this.quickSearchPending = false;
      this.UpdateGroupsCount();
    }, 300);
  }

  private MatchesQuickSearch(project: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    const teamMemberNames = (project?.team_members || []).map((tm: any) => tm?.display_name);
    const values = [
      project?.title,
      project?.linked_name,
      project?.project_leader_name,
      project?.description,
      project?.health_label,
      this.GetInternalStatusByGuid(project?.internal_status)?.title,
      ...teamMemberNames
    ];
    return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
  }

  JSONDebug(data) {
    return JSON.stringify(data);
  }

  //This takes in a project and if its fake we modify the project to be real
  async CreateFromFakeProject(project) {
    if (project.is_fake) {
      await this.globalService.presentLoading('Activating Project', 99999);
      let fake_guid = project.guid;
      //Create the project
      let create_data: any = await this.apiService.post('projects', project);
      if (create_data.success) {
        create_data.data[0].loaded_details = true;
        create_data.data[0].is_fake = false;
        Object.assign(project, create_data.data[0]);
        this.updateExpandedRowKeys(fake_guid, project.guid);
      }
      else {
        if (create_data.status_code == 208) {
          let existing_guid = create_data.data;
          let data: any = await this.apiService.get(`projects/${existing_guid}`);
          if (data.success) project = data.data[0];
          this.updateExpandedRowKeys(fake_guid, project.guid);
        }
      }
      this.globalService.dismissLoading();
    }
  }

  private updateExpandedRowKeys(oldGuid: string, newGuid: string) {
    // Update expanded row keys for all tables
    if (this.tables) {
      this.tables.forEach(table => {
        if (table.expandedRowKeys && table.expandedRowKeys[oldGuid]) {
          table.expandedRowKeys[oldGuid] = false;
          table.expandedRowKeys[newGuid] = true;
        }
      });
    }
  }

  /**
   * Save filter preferences to cookies
   */
  UpdateFilterCookies() {
    if(this.ignore_cookies) return;
    const filterPreferences = {
      groupView: this.groupView,
      show_completed_projects: this.show_completed_projects,
      show_in_progress_only: this.show_in_progress_only,
      show_overdue_only: this.show_overdue_only,
      hide_empty_groups: this.hide_empty_groups,
      hideColumns: this.hideColumns,
      columnOrder: this.availableColumns.map(col => col.key),
      project_person_filter: this.project_person_filter,
      view_team_member_guid: this.view_team_member_guid,
      view_team_member_role: this.view_team_member_role,
      group_by: this.group_by,
      max_per_page: this.max_per_page
    };
    this.cookieService.set('projectTableFilters', JSON.stringify(filterPreferences), 30, '/');

  }

  // Rows-per-page changed from the paginator's own dropdown - PrimeNG doesn't have a two-way [(rows)]
  // binding, so this is caught via (onPage) instead, same as any other filter change.
  private restored_max_per_page = false;
  OnTablePageChange(event: any) {
    if (event?.rows && event.rows !== this.max_per_page) {
      this.max_per_page = event.rows;
      this.UpdateFilterCookies();
    }
  }

  /**
   * Restore filter preferences from cookies
   */
  RestoreFilterCookies() {
    if(this.ignore_cookies) return;
    const cookieValue = this.cookieService.get('projectTableFilters');

    if (cookieValue) {
      try {
        const filterPreferences = JSON.parse(cookieValue);

        // Restore each filter setting
        // Don't restore groupView when searching
        if (!this.is_searching && filterPreferences.groupView !== undefined) {
          this.groupView = filterPreferences.groupView;
        }
        if (filterPreferences.show_completed_projects !== undefined) {
          this.show_completed_projects = filterPreferences.show_completed_projects;
        }
        if (filterPreferences.show_in_progress_only !== undefined) {
          this.show_in_progress_only = filterPreferences.show_in_progress_only;
        }
        if (filterPreferences.show_overdue_only !== undefined) {
          this.show_overdue_only = filterPreferences.show_overdue_only;
        }
        if (filterPreferences.hide_empty_groups !== undefined) {
          this.hide_empty_groups = filterPreferences.hide_empty_groups;
        }
        if (filterPreferences.project_person_filter !== undefined) {
          this.project_person_filter = filterPreferences.project_person_filter;
        }
        if (filterPreferences.view_team_member_guid !== undefined) {
          this.view_team_member_guid = filterPreferences.view_team_member_guid;
        }
        if (filterPreferences.view_team_member_role !== undefined) {
          this.view_team_member_role = filterPreferences.view_team_member_role;
        }
        // Only meaningful (and only reachable via the UI) when group_type_change is on - ignored
        // otherwise so a firm without the feature can't get stuck on a "health" grouping they have
        // no toggle to switch back out of.
        if (this.group_type_change && filterPreferences.group_by !== undefined) {
          this.group_by = filterPreferences.group_by;
          // Overrides a restored hide_empty_groups: false if the restored mode requires it locked on.
          if (this.IsCompanyOrContactGrouping) this.hide_empty_groups = true;
        }
        // Restore hidden columns
        if (filterPreferences.hideColumns !== undefined) {
          this.hideColumns = filterPreferences.hideColumns;
          this.syncColumnsFromHideColumns();
        }
        if (Array.isArray(filterPreferences.columnOrder)) this.ApplyColumnOrder(filterPreferences.columnOrder);
        if (filterPreferences.max_per_page !== undefined) {
          this.max_per_page = filterPreferences.max_per_page;
          this.restored_max_per_page = true;
        }
        // if (filterPreferences.tasks_with_my_skills !== undefined) {
        //   this.tasks_with_my_skills = filterPreferences.tasks_with_my_skills;
        // }

        
      } catch (e) {
        console.error('Error parsing filter preferences cookie:', e);
      }
    }
  }


  /**
   * Filter projects by selected tags
   * @param projectTags - Array of tags from the project (the field being filtered)
   * @param selectedTagGuids - Array of selected tag GUIDs from the filter
   */
  filterByTagGuid(projectTags: any, selectedTagGuids: any): boolean {
    // If no tags selected in filter, show all projects
    if (!selectedTagGuids || selectedTagGuids.length === 0) {
      return true;
    }

    // If project has no tags, hide it
    if (!projectTags || projectTags.length === 0) {
      return false;
    }

    // Check if any of the project's tags match the selected tags
    return projectTags.some((tag: any) => selectedTagGuids.includes(tag.guid));
  }

  SolveProjectStatus(project) {
    if(project.status == -1) {
      if(project.progress == 0) return "Inactive"
      else return "Active";
      }
      else if (project.status == 0 || project.status == 2) {
          return 'Active'
      } else if (project.status == 1) {
          return 'Completed'
      } else if (project.status == 21) {
          return 'Awaiting Missing Info'
      }

    return "";

  }

  /**
   * Initialize column visibility based on hideColumns input
   */
  initializeColumnVisibility() {
    // Apply any hideColumns passed as input
    this.syncColumnsFromHideColumns();
  }

  // Puts the columns in the order the column picker left them (saved with the other table preferences). Columns the
  // saved order doesn't know about - added since - go after the known ones, in their default order.
  private ApplyColumnOrder(order: string[]) {
    // An order saved before Client Year End Date existed: it goes right after the client's name
    if (!order.includes('client_year_end_date')) {
      order = [...order];
      const linked = order.indexOf('linked_name');
      if (linked >= 0) order.splice(linked + 1, 0, 'client_year_end_date');
    }
    const position = (key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
    this.availableColumns = [...this.availableColumns].sort((a, b) => position(a.key) - position(b.key));
  }

  /**
   * Sync availableColumns visibility state from hideColumns array
   */
  syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => {
      col.visible = !this.hideColumns.includes(col.key);
    });
  }

  /**
   * Sync hideColumns array from availableColumns visibility state
   */
  syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
  }

  // ── Date inline editing ───────────────────────────────────────────────────

  StartDateEdit(project: any, field: string, event: Event) {
    this.inline_edit_project = project;
    this.inline_edit_field = field;
    this.inline_edit_date = project[field] ? dayjs(project[field]).toDate() : null;

    if (field === 'internal_start_date') {
      this.inline_edit_min_date = null;
      this.inline_edit_max_date = project.internal_end_date ? dayjs(project.internal_end_date).toDate() : null;
    } else {
      // internal_end_date
      this.inline_edit_min_date = project.internal_start_date ? dayjs(project.internal_start_date).toDate() : null;
      this.inline_edit_max_date = null;
    }
    this.dateOverlay.show(event);
  }



  async SaveDateEdit() {
    const project = this.inline_edit_project;
    if (!project || !this.inline_edit_date) { this.CancelDateEdit(); return; }
    const field = this.inline_edit_field;
    const formatted = dayjs(this.inline_edit_date).format('YYYY-MM-DD');

    // Guard: internal_start_date must not be after internal_end_date
    if (field === 'internal_start_date' && project.internal_end_date && formatted > project.internal_end_date) {
      this.globalService.presentToastMessage('Project start date cannot be after the project end date.', false);
      return;
    }
    // Guard: internal_end_date must not be before internal_start_date
    if (field === 'internal_end_date' && project.internal_start_date && formatted < project.internal_start_date) {
      this.globalService.presentToastMessage('Project end date cannot be before the project start date.', false);
      return;
    }

    project[field] = formatted;
    this.dateOverlay.hide();
    this.inline_edit_project = null;
    this.inline_edit_field = null;
    this.inline_edit_date = null;
    this.inline_edit_min_date = null;
    this.inline_edit_max_date = null;
    this.inline_edit_disabled_tooltip = null;
    try {
      await this.apiService.put(`projects/${project.guid}`, project);
      this.globalService.presentToastMessage('Date updated successfully', true);
      this.RefreshProjectHealth(project);
      this.projectModified.emit({ data: project });
    } catch (e) {
      this.globalService.presentToastMessage('Failed to update date', false);
    }
  }

  // Re-scores one row after an inline change (FormatData scores every row once per load - see project.health)
  private RefreshProjectHealth(project: any) {
    const health = this.projectHealthService.Evaluate(project);
    project.health = health;
    project.health_label = health ? health.level.label : null;
    project.health_score = health ? health.score : null;
  }

  CancelDateEdit() {
    this.inline_edit_project = null;
    this.inline_edit_field = null;
    this.inline_edit_date = null;
    this.inline_edit_min_date = null;
    this.inline_edit_max_date = null;
    this.inline_edit_disabled_tooltip = null;
    if (this.dateOverlay) this.dateOverlay.hide();
  }

  StartEstimatedTimeEdit(project: any, event: Event) {
    this.inline_edit_est_project = project;
    const total = Number(project.estimated_time) || 0;
    this.inline_edit_est_hours = Math.floor(total / 60);
    this.inline_edit_est_minutes = total % 60;
    this.estimatedTimeOverlay.show(event);
  }

  async SaveEstimatedTimeEdit() {
    const project = this.inline_edit_est_project;
    if (!project) { this.CancelEstimatedTimeEdit(); return; }
    const totalMinutes = (Number(this.inline_edit_est_hours) || 0) * 60 + (Number(this.inline_edit_est_minutes) || 0);
    project.estimated_time = totalMinutes;
    project.estimated_time_remaining = totalMinutes - (Number(project.time_recorded) || 0);
    this.estimatedTimeOverlay.hide();
    this.inline_edit_est_project = null;
    try {
      await this.apiService.put(`projects/${project.guid}`, project);
      this.globalService.presentToastMessage('Estimated time updated', true);
      this.projectModified.emit({ data: project });
    } catch (e) {
      this.globalService.presentToastMessage('Failed to update estimated time', false);
    }
  }

  CancelEstimatedTimeEdit() {
    this.inline_edit_est_project = null;
    if (this.estimatedTimeOverlay) this.estimatedTimeOverlay.hide();
  }

  // ── Mark project as complete ───────────────────────────────────────────

  IsAdminOrProjectLeader(project: any): boolean {
    if (this.globalService.GetCurrentUser().is_admin) return true;
    if (project.team_leader && project.team_leader.guid === this.globalService.GetCurrentUser().guid) return true;
    if (project.team_leader_guid && project.team_leader_guid === this.globalService.GetCurrentUser().guid) return true;
    return false;
  }

  async MarkProjectAsComplete(project: any) {
    const alert = await this.alertController.create({
      header: 'Force Complete Project',
      message: 'Are you sure you want to force complete this project? It will automatically mark all tasks and actions as complete. This action cannot be undone.',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading('Fetching project tasks...', 99999999);
            await this.ForceCompleteProject(project);
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage('Project marked as complete', true);
            this.projectModified.emit({ data: project });
          }
        }
      ]
    });
    await alert.present();
  }

  // Shared by MarkProjectAsComplete (single row) and BulkMarkProjectsAsComplete - fetches every task
  // on the project, then every step on every task, and marks them all complete. Caller owns the
  // loading indicator and confirmation dialog since a bulk run wraps many of these in a single one.
  private async ForceCompleteProject(project: any) {
    // Fetch full project to get all tasks
    let project_data: any = await this.apiService.get(`projects/${project.guid}`);
    let tasks = project_data?.data?.[0]?.tasks ?? [];

    // Fetch all task details in parallel to collect every step
    const task_details_list = await Promise.all(
      tasks.map((task: any) => this.apiService.get(`tasks/${task.guid}`))
    );

    const all_step_guids: string[] = [];
    for (const task_details of task_details_list) {
      const steps = (task_details as any)?.data?.[0]?.steps ?? [];
      for (const step of steps) {
        all_step_guids.push(step.guid);
      }
    }

    // Fire all step completion requests in parallel
    await Promise.all(
      all_step_guids.map(guid => this.apiService.put(`steps/${guid}/complete/true?skip_start=1`, null))
    );

    // Refresh project row
    let updated: any = await this.apiService.get(`projects/${project.guid}`);
    if (updated?.data?.[0]) {
      project.status = updated.data[0].status;
      project.progress = updated.data[0].progress;
    }
    this.RefreshProjectHealth(project);
  }

  // ── Bulk selection actions ──────────────────────────────────────────────

  ClearSelectedProjects() {
    this.selectedProjects = [];
    this.selectedProjectEvent.emit(this.selectedProjects);
  }

  private BulkCompletableProjects(): any[] {
    return (this.selectedProjects || []).filter(p => p.status != 1 && !p.paused && this.IsAdminOrProjectLeader(p));
  }

  async BulkMarkProjectsAsComplete() {
    const eligible = this.BulkCompletableProjects();
    if (eligible.length === 0) {
      this.globalService.presentToastMessage('None of the selected projects can be force completed.', false);
      return;
    }
    const skipped = this.selectedProjects.length - eligible.length;
    const alert = await this.alertController.create({
      header: 'Force Complete Projects',
      message: `Are you sure you want to force complete ${eligible.length} project(s)? It will automatically mark all tasks and actions as complete. This action cannot be undone.` +
        (skipped > 0 ? ` ${skipped} selected project(s) will be skipped (already complete, paused, or you don't have permission).` : ''),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            for (let i = 0; i < eligible.length; i++) {
              await this.globalService.presentLoading(`Completing "${eligible[i].title}" (${i + 1} of ${eligible.length})...`, 99999999);
              await this.ForceCompleteProject(eligible[i]);
              this.projectModified.emit({ data: eligible[i] });
            }
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Completed ${eligible.length} project(s)`, true);
            this.ClearSelectedProjects();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkChangeProjectInternalStatus(status_guid: string) {
    if (!this.selectedProjects || this.selectedProjects.length === 0) return;
    const projects = [...this.selectedProjects];
    const status_label = this.GetInternalStatusByGuid(status_guid)?.title || 'the selected status';
    const alert = await this.alertController.create({
      header: 'Change Internal Status',
      message: `Are you sure you want to set the internal status of ${projects.length} project(s) to "${status_label}"? This action cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Updating internal status for ${projects.length} project(s)...`, 99999999);
            await Promise.all(projects.map(project => {
              project.internal_status = status_guid;
              return this.apiService.put(`projects/${project.guid}`, { internal_status: project.internal_status });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Internal status updated for ${projects.length} project(s)`, true);
            this.bulk_internal_status_guid = '';
            this.ClearSelectedProjects();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkAssignTeamMember(member_guid: string) {
    if (!this.selectedProjects || this.selectedProjects.length === 0 || !member_guid) return;
    // Only a firm admin or the project's current team leader may reassign it (matches the
    // backend's UpdateProject check, which 403s anyone else touching the project at all).
    const projects = this.selectedProjects.filter(p => this.IsAdminOrProjectLeader(p));
    if (projects.length === 0) {
      this.globalService.presentToastMessage('You must be a firm admin or the current project leader to reassign the selected project(s).', false);
      return;
    }
    const skipped = this.selectedProjects.length - projects.length;
    const member_label = this.GetAssignedTeamMember(member_guid) || 'the selected team member';
    const alert = await this.alertController.create({
      header: 'Assign Project Leader',
      message: `Are you sure you want to assign "${member_label}" as the project leader for ${projects.length} project(s)? This will overwrite any team member currently assigned as the leader.` +
        (skipped > 0 ? ` ${skipped} selected project(s) will be skipped (you must be a firm admin or the current project leader).` : ''),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Assigning ${projects.length} project(s)...`, 99999999);
            await Promise.all(projects.map(project => {
              project.team_leader_guid = member_guid;
              project.project_leader_name = this.GetAssignedTeamMember(member_guid);
              this.projectModified.emit({ data: project });
              return this.apiService.put(`projects/${project.guid}`, { team_leader_guid: member_guid });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Assigned ${projects.length} project(s) to ${member_label}`, true);
            this.bulk_assigned_member_guid = '';
            this.ClearSelectedProjects();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkAddTag(tag_guid: string) {
    if (!this.selectedProjects || this.selectedProjects.length === 0 || !tag_guid) return;
    const projects = [...this.selectedProjects];
    const tag = this.tags?.find(t => t.guid === tag_guid);
    const tag_label = tag?.title || 'the selected tag';
    const alert = await this.alertController.create({
      header: 'Add Tag',
      message: `Add "${tag_label}" to ${projects.length} project(s)?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Tagging ${projects.length} project(s)...`, 99999999);
            await Promise.all(projects.map(project => {
              if (!project.tags) project.tags = [];
              if (!project.tags.some((t: any) => t.guid === tag_guid) && tag) project.tags.push(tag);
              this.projectModified.emit({ data: project });
              return this.apiService.post(`projects/tags/${project.guid}`, { guid: tag_guid });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Added "${tag_label}" to ${projects.length} project(s)`, true);
            this.bulk_tag_guid = '';
            this.ClearSelectedProjects();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkAddToGroup(group_guid: string) {
    if (!this.selectedProjects || this.selectedProjects.length === 0 || !group_guid) return;
    const projects = [...this.selectedProjects];
    const group = this.groups?.find(g => g.guid === group_guid);
    const group_label = group?.title || 'the selected group';
    const alert = await this.alertController.create({
      header: 'Add To Group',
      message: `Add ${projects.length} project(s) to "${group_label}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Adding ${projects.length} project(s) to group...`, 99999999);
            await Promise.all(projects.map(project => {
              if (!project.groups) project.groups = [];
              if (!project.groups.some((g: any) => g.guid === group_guid) && group) project.groups.push(group);
              this.projectModified.emit({ data: project });
              return this.apiService.put(`projects/${project.guid}`, { groups: [{ guid: group_guid }] });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Added ${projects.length} project(s) to "${group_label}"`, true);
            this.bulk_group_guid = '';
            this.ClearSelectedProjects();
          }
        }
      ]
    });
    await alert.present();
  }

  /**
   * Open the column picker modal
   */
  // Admin-only (the Manage button itself is disabled for non-admins - see table.component.html)
  // deep link straight to the Projects section of Manage Firm, rather than always landing on Team
  // Members (see ManageFirmPage.ngOnInit's ?section= handling).
  GoToManageFirm() {
    this.router.navigate(['/manage-firm'], { queryParams: { section: 'projects' } });
  }

  async OpenColumnPicker() {
    // Create a deep copy of columns to avoid modifying original until save
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    
    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        // Update available columns with the returned state
        this.availableColumns = data.data.columns;
        // Sync hideColumns array
        this.syncHideColumnsFromColumns();
        // Persist to cookies
        this.UpdateFilterCookies();
      }
    });

    return await modal.present();
  }

  // Admin-only (matches the other Manage-* buttons in this toolbar), and restricted to Table View -
  // Group View can render multiple simultaneous p-table instances with a collapsed group having no
  // live Table at all, so there's no single reliable "currently visible rows" to export (see
  // GetCurrentlyVisibleProjectRows).
  CanExportProjects(): boolean {
    if (!this.globalService.GetCurrentUser()?.is_admin) return false;
    if (!this.groupView) return true;
    // In Group View, a "current table" only exists unambiguously when exactly one group (or the
    // Non Grouped Projects bucket) is expanded - each rendered <p-table> is its own live Table
    // instance (see GetCurrentlyVisibleProjectRows), so this.tables.length IS the expanded-table
    // count, however deep the group nesting.
    return this.tables?.length === 1;
  }

  ExportDisabledMessage(): string {
    if (!this.globalService.GetCurrentUser()?.is_admin) return 'Must be a firm admin to export.';
    if (this.groupView && this.tables?.length !== 1) return 'Expand exactly one group to export.';
    return '';
  }

  async OpenExportPdf() {
    return this.OpenExportColumnPicker('pdf', false);
  }

  async OpenExportExcel() {
    return this.OpenExportColumnPicker('excel', false);
  }

  // Only meaningful once the user has multi-select turned on and at least one row checked -
  // mirrors CanExportProjects' admin/group-view gating on top of that.
  CanExportSelected(): boolean {
    return this.CanExportProjects() && this.bulk_select_active && this.selectedProjects.length > 0;
  }

  async OpenExportSelectedPdf() {
    return this.OpenExportColumnPicker('pdf', true);
  }

  async OpenExportSelectedExcel() {
    return this.OpenExportColumnPicker('excel', true);
  }

  /**
   * Open the column picker modal in "pick columns to export" mode, then hand the currently visible
   * (custom-filtered + PrimeNG-column-filtered) rows - or just the checked rows, if selected_only -
   * off to the matching export service.
   */
  private async OpenExportColumnPicker(format: 'pdf' | 'excel', selected_only: boolean) {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';

    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy,
        header: `Export ${selected_only ? 'Selected ' : ''}Projects to ${formatLabel}`,
        selectLabel: 'Select columns to include:',
        confirmLabel: 'Export'
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      const columns: ColumnConfig[] = data?.data?.columns;
      if (!columns) return;
      const visibleColumns = columns.filter(c => c.visible);
      if (format === 'pdf') this.ExportProjectsPdf(visibleColumns, selected_only);
      else this.ExportProjectsExcel(visibleColumns, selected_only);
    });

    return await modal.present();
  }

  private BuildProjectExportRows(columns: ColumnConfig[], selected_only: boolean): string[][] {
    const rows = selected_only ? this.selectedProjects : this.GetCurrentlyVisibleProjectRows();
    return rows.map(project => columns.map(c => this.FormatProjectColumnForExport(project, c.key)));
  }

  private ExportProjectsPdf(columns: ColumnConfig[], selected_only: boolean = false) {
    this.tablePdfExportService.Export('Projects', columns, this.BuildProjectExportRows(columns, selected_only), `projects-export-${dayjs().format('YYYY-MM-DD')}.pdf`);
  }

  private ExportProjectsExcel(columns: ColumnConfig[], selected_only: boolean = false) {
    this.tableExcelExportService.Export('Projects', columns, this.BuildProjectExportRows(columns, selected_only), `projects-export-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  }

  // Reads whatever's currently rendered on screen straight off each live PrimeNG Table instance -
  // filteredValue already reflects every p-columnFilter the user has applied, and .value is itself
  // the custom-toggle-filtered array (see GetFilteredProjects/HideRowLogic), so this needs no
  // filter-matching logic of its own. In Table View there's exactly one such instance; in Group View
  // CanExportProjects() only allows the export button once exactly one group is expanded, so this
  // still resolves to that one group's rows without any extra branching here.
  private GetCurrentlyVisibleProjectRows(): any[] {
    const seen = new Set<string>();
    const rows: any[] = [];
    this.tables?.forEach(t => {
      const tableRows: any[] = (t as any).filteredValue ?? t.value ?? [];
      for (const row of tableRows) {
        if (row?.guid) {
          if (seen.has(row.guid)) continue;
          seen.add(row.guid);
        }
        rows.push(row);
      }
    });
    return rows;
  }

  // Mirrors how each column actually renders in the table body (table.component.html) - see the
  // status_label/health_label notes on those two cases for why the precomputed filter fields are used
  // instead of re-deriving from raw codes.
  private FormatProjectColumnForExport(project: any, key: string): string {
    switch (key) {
      case 'title': return project.title || '';
      case 'internal_status': return this.GetInternalStatusByGuid(project.internal_status)?.title || '';
      case 'status': return project.status_label || '';
      case 'linked_name': return project.linked_name || '';
      case 'client_year_end_date': return project.client_year_end_date ? dayjs(project.client_year_end_date).format('MMM D') : '';
      case 'period_start_date': return project.period_start_date ? dayjs(project.period_start_date).format('MMM D, YYYY') : '';
      case 'period_end_date': return project.period_end_date ? dayjs(project.period_end_date).format('MMM D, YYYY') : '';
      case 'progress': return `${project.progress ?? 0}%`;
      case 'health': return project.health_label || '-';
      case 'internal_start_date': return project.internal_start_date ? dayjs(project.internal_start_date).format('MMM D, YYYY') : '';
      case 'internal_end_date': return project.internal_end_date ? dayjs(project.internal_end_date).format('MMM D, YYYY') : '';
      case 'estimated_time': return project.estimated_time != null ? `${project.estimated_time} mins` : '-';
      case 'estimated_time_remaining': return project.estimated_time_remaining != null ? `${project.estimated_time_remaining} mins` : '-';
      case 'completed_date': return project.completed_date ? dayjs(project.completed_date).format('MMM D, YYYY') : '';
      case 'tags': return (project.tags || []).map((t: any) => t.title).join(', ');
      case 'groups': return (project.groups || []).map((g: any) => g.title).join(', ');
      case 'document_request_complete': return project.document_request_complete ? dayjs(project.document_request_complete).format('MMM D, YYYY h:mm A') : '';
      case 'last_client_document_upload': return project.last_client_document_upload ? dayjs(project.last_client_document_upload).format('MMM D, YYYY h:mm A') : '';
      case 'client_role': return this.getClientRoleName(project.role_guid);
      case 'project_leader_name': return project.project_leader_name || '';
      default: return '';
    }
  }

  async OpenManualTimeEntry(project: any) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { project }
    });
    return await modal.present();
  }

  async ConfirmDiscardTimer(project: any) {
    const alert = await this.alertController.create({
      header: 'Discard Timer?',
      message: `Are you sure you want to discard the timer for "${project.title}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Discard', role: 'destructive', handler: () => this.globalService.ResetTimer() }
      ]
    });
    await alert.present();
  }

  async StopAndLogProject(project: any) {
    this.globalService.StopTimer();
    const now = new Date();
    const start = this.globalService.GetTimerStart(now);
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { initial_start_time: start, initial_end_time: now, initial_worked_ms: this.globalService.total_seconds, reset_timer_on_save: true, project }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.success) this.globalService.ResetTimer();
    });
    return await modal.present();
  }

  async StartTimerForProject(project: any) {
    if (this.globalService.timer_project?.guid === project.guid) {
      this.globalService.StartTimer();
      return;
    }
    const timerActive = this.globalService.timer_wall_start || this.globalService.total_seconds > 0;
    if (timerActive) {
      const alert = await this.alertController.create({
        header: 'Timer Already Running',
        message: 'A timer is already running. Restart it for "' + project.title + '"?',
        buttons: [
          { text: 'Keep Current', role: 'cancel' },
          {
            text: 'Restart',
            handler: () => {
              this.globalService.ResetTimer();
              this.globalService.timer_project = project;
              this.globalService.timer_project_type = 'project';
              this.globalService.StartTimer();
              this.globalService.presentToastMessage('Timer started for: ' + project.title, true);
            }
          }
        ]
      });
      return await alert.present();
    }
    this.globalService.timer_project = project;
    this.globalService.timer_project_type = 'project';
    this.globalService.StartTimer();
    this.globalService.presentToastMessage('Timer started for: ' + project.title, true);
  }

}
